import { Component } from '@theme/component';

const MOTO_STORAGE_KEY = 'starbike:selectedMoto';
const MOTO_CHANGE_EVENT = 'starbike:moto-change';
const FILTER_INPUT_FLAG = 'data-moto-filter';
const HIDE_CLASS = 'moto-filter-hidden';
const EMPTY_STATE_ID = 'inline-search-no-compat';

function readSavedMoto() {
  try {
    const parsed = JSON.parse(localStorage.getItem(MOTO_STORAGE_KEY) || 'null');
    if (!parsed || typeof parsed.gid !== 'string' || !parsed.gid.startsWith('gid://')) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Inline expandable search trigger for the header.
 *
 * Wraps a <predictive-search-component> with show/hide controls anchored to a
 * trigger button. Live results, keyboard navigation, and the submit→/search
 * fallback all come from the underlying predictive-search-component.
 *
 * Also bridges the motorcycle compatibility filter into search:
 * - Maintains hidden filter inputs on the form so /search receives the param
 * - Hides incompatible cards in the predictive dropdown via MutationObserver
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger - The icon button that opens the search.
 * @property {HTMLElement} searchComponent - The <predictive-search-component> element.
 * @extends {Component<Refs>}
 */
class InlineSearch extends Component {
  requiredRefs = ['trigger', 'searchComponent'];

  #controller = new AbortController();
  /** @type {MutationObserver | null} */
  #resultsObserver = null;
  /** @type {boolean} */
  #bypassFilterOnce = false;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;

    document.addEventListener('click', this.#handleDocumentClick, { signal, capture: true });
    document.addEventListener('keydown', this.#handleKeydown, { signal });

    document.addEventListener(MOTO_CHANGE_EVENT, this.#onMotoChange, { signal });
    window.addEventListener('storage', this.#onStorage, { signal });

    const form = this.#form;
    if (form) form.addEventListener('submit', this.#onFormSubmit, { signal });

    this.#syncFilterInputs();
    this.#observeResults();
    this.#applyDropdownFilter();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#resultsObserver?.disconnect();
    this.#resultsObserver = null;
  }

  get isOpen() {
    return this.hasAttribute('data-open');
  }

  get #filterParam() {
    return this.dataset.motoFilterParam || 'filter.p.m.custom.compatible_motorcycles';
  }

  get #form() {
    return this.querySelector('form');
  }

  open = () => {
    if (this.isOpen) return;
    this.setAttribute('data-open', '');
    this.refs.trigger.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      const input = this.querySelector('input[name="q"]');
      if (input instanceof HTMLInputElement) input.focus();
    });
  };

  close = () => {
    if (!this.isOpen) return;
    this.removeAttribute('data-open');
    this.refs.trigger.setAttribute('aria-expanded', 'false');

    const panel = /** @type {any} */ (this.refs.searchComponent);
    if (panel && typeof panel.resetSearch === 'function') {
      panel.resetSearch(false);
    }

    this.refs.trigger.focus({ preventScroll: true });
  };

  toggle = () => {
    if (this.isOpen) this.close();
    else this.open();
  };

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    if (!this.isOpen) return;
    const target = event.composedPath?.()[0] ?? event.target;
    if (target instanceof Node && this.contains(target)) return;
    this.close();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.isOpen) {
      event.stopPropagation();
      this.close();
    }
  };

  #onMotoChange = () => {
    this.#syncFilterInputs();
    this.#applyDropdownFilter();
  };

  /** @param {StorageEvent} event */
  #onStorage = (event) => {
    if (event.key !== MOTO_STORAGE_KEY) return;
    this.#syncFilterInputs();
    this.#applyDropdownFilter();
  };

  /**
   * Sync the form's hidden filter inputs to the saved motorcycle. When a moto
   * is selected, we add one input for the selected GID and (if defined) one
   * for the universal "toutes-motos" GID — mirroring the collection-page
   * redirect logic in theme.liquid.
   */
  #syncFilterInputs() {
    const form = this.#form;
    if (!form) return;

    if (this.#bypassFilterOnce) {
      this.#removeFilterInputs(form);
      return;
    }

    const saved = readSavedMoto();
    this.#removeFilterInputs(form);
    if (!saved) return;

    const param = this.#filterParam;
    const universal = window.STARBIKE_UNIVERSAL_GID;

    form.appendChild(this.#makeFilterInput(param, saved.gid));
    if (universal && universal !== saved.gid) {
      form.appendChild(this.#makeFilterInput(param, universal));
    }
  }

  /**
   * @param {HTMLFormElement} form
   */
  #removeFilterInputs(form) {
    for (const el of form.querySelectorAll(`input[${FILTER_INPUT_FLAG}]`)) el.remove();
  }

  /**
   * @param {string} name
   * @param {string} value
   */
  #makeFilterInput(name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.setAttribute(FILTER_INPUT_FLAG, '');
    return input;
  }

  /**
   * Re-run the filter pass after every morph of the results container.
   */
  #observeResults() {
    const target = this.refs.searchComponent;
    if (!target || this.#resultsObserver) return;

    this.#resultsObserver = new MutationObserver(() => this.#applyDropdownFilter());
    this.#resultsObserver.observe(target, { childList: true, subtree: true });
  }

  /**
   * Hide every `[data-compatible-motorcycles]` card whose list doesn't include
   * the selected moto GID or the universal GID. If every card in a group is
   * hidden, hide its group container too. If the result set is non-empty but
   * we've hidden every product, show the bike-aware empty state.
   */
  #applyDropdownFilter() {
    const saved = readSavedMoto();
    const cards = this.querySelectorAll('[data-compatible-motorcycles]');

    if (!saved) {
      for (const card of cards) {
        card.classList.remove(HIDE_CLASS);
      }
      this.#toggleEmptyGroups();
      this.#removeNoCompatBanner();
      return;
    }

    const allowed = new Set([saved.gid]);
    if (window.STARBIKE_UNIVERSAL_GID) allowed.add(window.STARBIKE_UNIVERSAL_GID);

    let visibleCount = 0;
    for (const card of cards) {
      const raw = card.getAttribute('data-compatible-motorcycles') || '';
      const gids = raw ? raw.split(',') : [];
      const match = gids.some((g) => allowed.has(g));
      card.classList.toggle(HIDE_CLASS, !match);
      if (match) visibleCount++;
    }

    this.#toggleEmptyGroups();

    const hasResultsContainer = this.querySelector('[data-search-results]');
    const noResultsAlready = this.querySelector('.predictive-search-results__no-results');
    if (cards.length > 0 && visibleCount === 0 && hasResultsContainer && !noResultsAlready) {
      this.#showNoCompatBanner(saved);
    } else {
      this.#removeNoCompatBanner();
    }
  }

  /**
   * Hide a product group (its heading + list) when all children are filtered
   * out. We target `.predictive-search-results__products` — the wrapping div
   * that contains the heading + the ul in both the real-results and
   * recently-viewed renderings — so the heading doesn't dangle when its list
   * is empty.
   */
  #toggleEmptyGroups() {
    const groups = this.querySelectorAll('.predictive-search-results__products');
    for (const group of groups) {
      const items = group.querySelectorAll('[data-compatible-motorcycles]');
      if (items.length === 0) {
        group.classList.remove(HIDE_CLASS);
        continue;
      }
      const anyVisible = Array.from(items).some((i) => !i.classList.contains(HIDE_CLASS));
      group.classList.toggle(HIDE_CLASS, !anyVisible);
    }
  }

  /**
   * Show a "no compatible matches for your bike" banner with a bypass button.
   * @param {{label?:string, year?:string}} saved
   */
  #showNoCompatBanner(saved) {
    let banner = this.querySelector(`#${EMPTY_STATE_ID}`);
    if (banner instanceof HTMLElement) return; // already shown

    const content = this.querySelector('.predictive-search-form__content');
    if (!(content instanceof HTMLElement)) return;

    const tpl = this.dataset.noCompatTemplate || 'Aucun résultat compatible avec votre {bike}.';
    const bikeLabel = [saved.label, saved.year].filter(Boolean).join(' ').trim() || '';
    const message = tpl.replace('__BIKE__', bikeLabel);
    const buttonLabel = this.dataset.searchWithoutFilterLabel || 'Voir tous les résultats';

    banner = document.createElement('div');
    banner.id = EMPTY_STATE_ID;
    banner.className = 'inline-search__no-compat';
    banner.innerHTML = `
      <p class="inline-search__no-compat-text"></p>
      <button type="button" class="button inline-search__no-compat-bypass"></button>
    `;
    const p = banner.querySelector('.inline-search__no-compat-text');
    if (p) p.textContent = message;
    const btn = banner.querySelector('button');
    if (btn) {
      btn.textContent = buttonLabel;
      btn.addEventListener('click', this.#onBypassClick);
    }

    content.prepend(banner);
  }

  #removeNoCompatBanner() {
    this.querySelector(`#${EMPTY_STATE_ID}`)?.remove();
  }

  /**
   * One-shot bypass of the moto filter: strip the hidden inputs and submit.
   */
  #onBypassClick = () => {
    const form = this.#form;
    if (!form) return;
    this.#bypassFilterOnce = true;
    this.#removeFilterInputs(form);
    form.requestSubmit();
  };

  /**
   * If a normal submit happens and we previously bypassed once, reset the flag
   * so subsequent opens re-apply the filter.
   */
  #onFormSubmit = () => {
    this.#bypassFilterOnce = false;
  };
}

if (!customElements.get('inline-search')) {
  customElements.define('inline-search', InlineSearch);
}
